import { themeGet } from '@styled-system/theme-get';
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { HybridAvailability, SeriesAvailability } from '../../SeriesAvailability';
import { ReactComponent as GreenAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Green.svg';
import { ReactComponent as YellowAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Yellow.svg';
import { ReactComponent as RedAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Red.svg';
import { Dropdown } from '../../../../components/Dropdown/Dropdown';
import { ISeriesWithHybrid } from '../../../../logic/store/Seeds/SeedsActions';
import { IOptionItem } from '../../../../components/Dropdown/IOptionItem';
import { Checkbox } from '../../../../components/Checkbox/Checkbox';
import { ICropPlanSelectedProduct } from '../../../../logic/Models/Responses/FieldPlanResponse';
import { useCropPlanTrackingState } from '../useCropPlanTrackingState';
import { useAmplitudePassthrough } from '../../../../logic/Utility/useAmplitude';
import { useScopedSession } from '../../../../tracing/session';
import { EventStructureTags } from '../../../../tracing/EventTagNames';
import { EventFieldPlanNames } from '../../../../tracing/EventNames';
import { HybridAvailability7 } from '../../../../logic/Models/Responses/SeedResponseV7';
import { autoSelectTreatmentForHybrid } from '../../../../logic/store/Seeds/SeedLookups';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../logic/store/Store';
import { ReactComponent as AlertIcon } from '../../Icons/AlertIcon.svg';
import { Popover } from 'antd';

const SeriesRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 80px;
	min-height: 80px;
	background-color: ${themeGet('colors.white')};
	border: 1px solid ${themeGet('colors.lightestGrey')};
	border-radius: 5px;
	margin-bottom: 8px;
	align-items: center;
	box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
`;

const SeriesData = styled.div`
	color: ${themeGet('colors.darkGrey')};
	font-family: ${themeGet('fonts.base')};
	font-size: ${themeGet('fontSizes.extraSmall')};
	font-weight: ${themeGet('fontWeights.bold')};
	padding: 0px 12px;
	padding-left: 15px;
	width: 100%;
`;

export interface ISeriesWithHybridRowProps
{
	selected: boolean; // Whether the series is selected in the list
	series: ISeriesWithHybrid; // The series that this row represents
	selectedHybrids: ICropPlanSelectedProduct[];
	selectHybridForSeries: (seriesId: string, hybridId: string, treatment: string, treatmentId: string) => void;
	selectSingleSeries: (selected: boolean, seriesId: string, hybridId: string, treatment: string, treatmentId: string) => void;
}

/**
 * The Series With Hybrid/Treatment list dropdown and checkbox 
 */
export const SeriesWithHybridRow = (props: ISeriesWithHybridRowProps) =>
{
	const {
		selected,
		series,
		selectedHybrids,
		selectHybridForSeries,
		selectSingleSeries
	} = props;

	const theme = useTheme();

	const brandLookup = useSelector((state: RootState) => state.seeds.brands)!;
	const hybridLookup = useSelector((state: RootState) => state.seeds.hybrids)!;

	const [hybridOptions, setHybridOptions] = useState<IOptionItem[]>([]);
	const [selectedHybridId, setSelectedHybridId] = useState<string>(undefined);
	const hybrid = series.hybrids.find(h => h.HybridId === selectedHybridId);

	const [treatmentOptions, setTreatmentOptions] = useState<IOptionItem[]>([]);
	const [selectedTreatmentId, setSelectedTreatmentId] = useState<string>(undefined);
	const [hybridTreatmentError, setHybridTreatmentError] = useState<boolean>(false);

	const trackingData = useCropPlanTrackingState();
	const session = useScopedSession(SeriesWithHybridRow.name, trackingData, {
		[EventStructureTags.PageContext]: 'series_with_hybrid_row',
		[EventStructureTags.PageUrl]: window.location.toString()
	});

	const getTreatmentOptions = (hybrid: HybridAvailability7) =>
	{
		if (hybrid)
		{
			const filteredTreatments = Object.values(hybrid.Treatments).filter(h => h.Availability!== 'None' && h.Availability !== 'Unknown');
			const createTreatmentOptions = filteredTreatments.map(treatment =>
			{
				const treatmentOption: IOptionItem =
				{
					// Add the availability icon
					icon: <HybridAvailability style={{width: 14, marginRight: 6}}>
						{treatment.Availability == 'Green' ? <GreenAvailabilityIcon style={{ fill: theme.colors.availabilityGreen }} />
							: treatment.Availability === 'Yellow' ? <YellowAvailabilityIcon style={{ fill: theme.colors.availabilityYellow }} />
								: <RedAvailabilityIcon style={{ fill: theme.colors.availabilityRed }} />
						}
					</HybridAvailability>,
					label: treatment.TreatmentName,
					value: treatment.TreatmentId,
				};
	
				return treatmentOption;
			});
	
			setTreatmentOptions(createTreatmentOptions);
		}
	};

	const selectHybrid = useAmplitudePassthrough(session, EventFieldPlanNames.ChangeRequestedTrait, (hybridId: string) =>
	{
		const foundHybrid = series.hybrids.find(h => h.HybridId === hybridId);
		const defaultTreatment = selectedTreatmentId ? foundHybrid.Treatments[selectedTreatmentId] 
			: autoSelectTreatmentForHybrid(foundHybrid, hybridLookup, brandLookup);

		setSelectedHybridId(hybridId);
		setSelectedTreatmentId(defaultTreatment?.TreatmentId);
		selectHybridForSeries(series.seriesId, hybridId, defaultTreatment?.TreatmentName, defaultTreatment?.TreatmentId);
		getTreatmentOptions(series.hybrids.find(h => h.HybridId === hybridId));
	}, [series, selectedTreatmentId, setSelectedHybridId, selectHybridForSeries, getTreatmentOptions, autoSelectTreatmentForHybrid]);

	const selectTreatment = useAmplitudePassthrough(session, EventFieldPlanNames.ChangeRequestedTreatmentForTrait, (treatmentId: string) =>
	{
		const treatment = series.hybrids.find(h => h.HybridId === selectedHybridId)?.Treatments[treatmentId];
		setSelectedTreatmentId(treatmentId);
		selectHybridForSeries(series.seriesId, selectedHybridId, treatment?.TreatmentName, treatmentId);
	}, [series, selectedHybridId, setSelectedTreatmentId, selectHybridForSeries]);

	const popOverContent = (
		<div style={{ fontWeight: theme.fontWeights.bold }}>
			The previously saved treatment is no longer available, it has been defaulted to the next available option.
		</div>
	);

	useEffect(() =>
	{
		if (series && series.hybrids && series.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown').length > 0)
		{
			const createHybridOptions = series.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown').map(hybrid =>
			{
				const hybridOption: IOptionItem =
				{
					// Add the availability icon
					icon: <HybridAvailability style={{width: 14, marginRight: 6}}>
						{hybrid.Availability == 'Green' ? <GreenAvailabilityIcon style={{ fill: theme.colors.availabilityGreen }} />
							: hybrid.Availability === 'Yellow' ? <YellowAvailabilityIcon style={{ fill: theme.colors.availabilityYellow }} />
								: <RedAvailabilityIcon style={{ fill: theme.colors.availabilityRed }} />
						}
					</HybridAvailability>,
					label: hybrid.HybridName,
					value: hybrid.HybridId
				};
	
				return hybridOption;
			});
	
			setHybridOptions(createHybridOptions);

			// Default Hybrid
			const defaultHybrid = series?.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown')[0];
			const defaultTreatmentForDefaultHybrid = autoSelectTreatmentForHybrid(defaultHybrid, hybridLookup, brandLookup);			

			// If the series is selected then we're either opening a previously created plan's seed choices or the
			// user has already selected it
			if (selected)
			{
				const newSelectedHybrid = series.hybrids.length > 1 ? 
					series.hybrids.find(h => selectedHybrids.find(sh => sh.HybridId === h.HybridId)) : undefined;
				const selectedHybridAsProduct = selectedHybrids.find(h => h.HybridId === newSelectedHybrid?.HybridId);
				const selectedProductTreatmentId = selectedHybridAsProduct?.TreatmentId; // The previously selected treatment id
				if (newSelectedHybrid)
				{
					setSelectedHybridId(newSelectedHybrid.HybridId);
					const defaultTreatmentForNewHybrid = autoSelectTreatmentForHybrid(newSelectedHybrid, hybridLookup, brandLookup);
					// Check to see if the previously selected treatment is still available for the selected hybrid
					const hybridAllTreatments = Object.values(newSelectedHybrid.Treatments);
					if (newSelectedHybrid && selectedProductTreatmentId)
					{
						const selectedTreatmentObject = hybridAllTreatments.find(t => t.TreatmentId === selectedProductTreatmentId);
						if (selectedTreatmentObject && (selectedTreatmentObject.Availability === 'None' || selectedTreatmentObject.Availability === 'Unknown'))
						{
							// We have a previously selected treatment but it's no longer available, so set the error icon and select the default treatment
							setSelectedTreatmentId(defaultTreatmentForNewHybrid?.TreatmentId);
							setHybridTreatmentError(true);
						}
						else
						{
							// Set the selected treatment because it is available
							setSelectedTreatmentId(selectedProductTreatmentId);
						}
					}
					else
					{
						// Set the defaulted treatment
						setSelectedTreatmentId(selectedHybridAsProduct ? selectedProductTreatmentId ?? defaultTreatmentForNewHybrid?.TreatmentId : defaultTreatmentForNewHybrid?.TreatmentId);
					}
					// Set the treatment options based on the hybrid selection
					getTreatmentOptions(newSelectedHybrid);
				}
				else
				{
					setSelectedHybridId(selectedHybridId ? selectedHybridId : defaultHybrid.HybridId);
					const selectedHybridAsProduct = selectedHybrids.find(h => h.HybridId === selectedHybridId ?? defaultHybrid.HybridId);
					const selectedProductTreatmentId = selectedHybridAsProduct?.TreatmentId;
					const defaultTreatmentForHybrid = autoSelectTreatmentForHybrid(hybrid, hybridLookup, brandLookup);

					// Check to see if the previously selected treatment is still available for the selected hybrid
					const hybridAllTreatments = Object.values(hybrid.Treatments);
					if (selectedHybridAsProduct && selectedProductTreatmentId)
					{
						const selectedTreatmentObject = hybridAllTreatments.find(t => t.TreatmentId === selectedProductTreatmentId);
						
						if (selectedTreatmentObject && (selectedTreatmentObject.Availability === 'None' || selectedTreatmentObject.Availability === 'Unknown'))
						{
							// We have a previously selected treatment but it's no longer available, so set the error icon and select the default treatment
							setSelectedTreatmentId(defaultTreatmentForHybrid?.TreatmentId);
							setHybridTreatmentError(true);
						}
						else
						{
							// Set the selected treatment because it is available
							setSelectedTreatmentId(selectedProductTreatmentId);
						}
					}
					else
					{
						// Set the defaulted treatment
						setSelectedTreatmentId(selectedHybridAsProduct ? selectedProductTreatmentId ?? defaultTreatmentForHybrid?.TreatmentId : defaultTreatmentForHybrid?.TreatmentId);
					}
					
					// Set the treatment options based on the hybrid selection
					getTreatmentOptions(defaultHybrid);
				}
			}
			else
			{
				// The default selection of a hybrid for a seed
				if (!selectedHybridId)
				{
					setSelectedHybridId(defaultHybrid.HybridId);

					// Set the treatment options based on the hybrid selection
					getTreatmentOptions(defaultHybrid);

					// Set the defaulted treatment
					setSelectedTreatmentId(defaultTreatmentForDefaultHybrid?.TreatmentId);
				}
			}
		}
		
	}, [series, hybrid, selected]);

	const toggleSeriesSelected = (checked: boolean) =>
	{
		// Default Hybrid & Treatment - if we have a selected hybrid and/or selected treatment, use those first
		const defaultHybrid = selectedHybridId ? series.hybrids.find(h => h.HybridId === selectedHybridId) :
			series?.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown')[0];
		const defaultTreatment = selectedTreatmentId ? defaultHybrid.Treatments[selectedTreatmentId] 
			: autoSelectTreatmentForHybrid(defaultHybrid, hybridLookup, brandLookup);
		selectSingleSeries(checked, series.seriesId, selectedHybridId ?? defaultHybrid.HybridId, defaultTreatment.TreatmentName, defaultTreatment.TreatmentId);
	};

	return (
		<SeriesRow className='SeriesContainer' key={series.seriesId + 'FilteredSeriesList'}>
			<SeriesAvailability className='SeriesAvailability'>
				{hybrid?.Availability == 'Green' ? <GreenAvailabilityIcon />
					: hybrid?.Availability === 'Yellow' ? <YellowAvailabilityIcon />
						: <RedAvailabilityIcon />
				}
			</SeriesAvailability>
			<div style={{ display: 'flex', flexDirection: 'column', width: '65%', minWidth: '65%', maxWidth: '65%' }}>
				{
					hybridOptions && hybridOptions.length > 1 ?
						<Dropdown
							variant='outlinednarrow'
							options={hybridOptions}
							onSelect={selectHybrid}
							selected={selectedHybridId}
							className='Hybrid_Dropdown'
							containerWidth={'100%'}
							titleFontSize={theme.fontSizes.extraSmall}
							relativePosition={true}
						/>
						:
						<SeriesData className='HybridName'>
							{series?.hybrids?.find(h => h.HybridId === selectedHybridId)?.HybridName}
						</SeriesData>
				}
				{
					hybrid && hybrid.Treatments && treatmentOptions.length > 1 ?
						<Dropdown
							variant='outlinednarrow'
							options={treatmentOptions}
							onSelect={selectTreatment}
							selected={selectedTreatmentId}
							className='Treatment_Dropdown'
							containerWidth={'100%'}
							titleFontSize={theme.fontSizes.extraSmall}
							relativePosition={true}
							menuStyle={{ width: '130%' }}
						/>
						:
						<SeriesData
							className='TreatmentName'
							style={{ fontWeight: theme.fontWeights.normal, fontSize: theme.fontSizes.tiny }}
						>
							{treatmentOptions[0]?.label}
						</SeriesData>
				}
			</div>
			<div style={{ display: 'flex', flexDirection: 'column', width: '40%', alignItems: 'center' }}>
				{
					hybridTreatmentError &&
					<Popover content={popOverContent} trigger='click'>
						<AlertIcon width={35} height={35} style={{ cursor: 'pointer' }} />
					</Popover>
				}
				<div style={{ display: 'flex', flexDirection: 'row', paddingLeft: 10, paddingRight: 10 }}>
					<div className='SeriesRm' style={{ fontWeight: theme.fontWeights.bold, paddingRight: 2 }}>
						{series.relativeMaturity}
					</div>
					<div style={{ marginLeft: 'auto' }}>
						<Checkbox checked={selected} onChange={e => toggleSeriesSelected(e.target.checked)} />
					</div>
				</div>
			</div>
		</SeriesRow>
	);
};